import React, { useState } from 'react';
import { Edit, CheckCircle, ExternalLink, Shield, Link2 } from 'lucide-react';
import Button from './Buttons';
import RiskManagementModal from './RiskManagementModal';
import RiskAcceptanceModal from './RiskAcceptanceModal';
import RiskApprovalModal from './RiskApprovalModal';
import AssignRiskModal from './AssignRiskModal';
import CreateJiraIssueModal from './CreateJiraIssueModal';
import CreateServiceNowIncidentModal from './CreateServiceNowIncidentModal';
import { useRiskWorkflow } from '../hooks/useRiskWorkflow';
import { riskWorkflowEndpoints, toWorkflowRisk, ticketTypeOf, ACCEPTANCE_STATUSES } from '../utils/riskWorkflow';

// The workflow actions on one risk (Manage, ServiceNow, Jira, Assign / Reassign, Accept Risk,
// Approve) and their dialogs. One implementation for every page that works a risk; the gates are
// useRiskWorkflow's, the endpoints the unified risk controller's.
//
// risk: a RiskDetailsDto. onChanged: reload after any dialog changes the risk. onAssignSuccess: the
// assign dialog's result, for a page that patches its list in place. disabledReason: when set, every
// action is disabled with that tooltip (e.g. while risks are selected for bulk assignment).
// leading / trailing: a page's own buttons in the same row (before Manage / after the last action);
// a user who may not update the risk still sees them, without the workflow actions.
const RiskWorkflowActions = ({ risk, onChanged, onAssignSuccess, disabledReason = null, className = 'mt-3 pt-3 border-t border-gray-100', leading = null, trailing = null }) => {
    const { canManageRisk, canUpdateRisk, canApproveRisk, ticketingAvailable } = useRiskWorkflow();
    // Which dialog is open; the dialog always reads the current risk prop, so it sees fresh data
    // after a reload.
    const [open, setOpen] = useState(null);
    const close = () => setOpen(null);

    if (!canUpdateRisk(risk)) {
        if (!leading && !trailing) return null;
        return <div className={className}><div className="flex flex-wrap gap-2">{leading}{trailing}</div></div>;
    }

    const endpoints = riskWorkflowEndpoints(risk.id);
    const disabled = !!disabledReason;
    const title = disabledReason || undefined;
    const workflowRisk = toWorkflowRisk(risk);
    const ticketRisk = { ...workflowRisk, remediationType: ticketTypeOf(risk.riskType) };

    return (
        <>
            <div className={className}>
                {/* Acceptance expiry (UN EOI PR 5) — computed server-side */}
                {risk.isAcceptanceExpired && (
                    <div className="flex items-center gap-2 mb-2 text-sm">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-300">
                            Acceptance expired
                        </span>
                        <span className="text-red-600">Re-review required — reopen or re-accept with a new expiry date.</span>
                    </div>
                )}
                <div className="flex flex-wrap gap-2">
                    {leading}
                    <Button size="sm" variant="primary" disabled={disabled} title={title} onClick={() => setOpen('manage')}>
                        <Edit className="h-4 w-4 mr-2" />
                        Manage
                    </Button>

                    {ticketingAvailable && canManageRisk(risk) && (
                        <Button size="sm" variant={risk.serviceNowIncidentNumber ? 'success' : 'primary'}
                            disabled={disabled} title={title} onClick={() => setOpen('servicenow')}>
                            {risk.serviceNowIncidentNumber ? (
                                <><ExternalLink className="h-4 w-4 mr-2" />{risk.serviceNowIncidentNumber}</>
                            ) : (
                                <><Link2 className="h-4 w-4 mr-2" />ServiceNow</>
                            )}
                        </Button>
                    )}

                    {ticketingAvailable && canManageRisk(risk) && (
                        <Button size="sm" variant={risk.jiraIssueKey ? 'success' : 'primary'}
                            disabled={disabled} title={title} onClick={() => setOpen('jira')}>
                            {risk.jiraIssueKey ? (
                                <><ExternalLink className="h-4 w-4 mr-2" />{risk.jiraIssueKey}</>
                            ) : (
                                <><Link2 className="h-4 w-4 mr-2" />Jira</>
                            )}
                        </Button>
                    )}

                    <Button size="sm" variant="primary" disabled={disabled} title={title} onClick={() => setOpen('assign')}>
                        <Shield className="h-4 w-4 mr-2" />
                        {(risk.remediationAssignTo || risk.assignedToUserId) ? 'Reassign' : 'Assign'}
                    </Button>

                    <Button size="sm" variant="danger"
                        disabled={disabled || ACCEPTANCE_STATUSES.includes(risk.status) || risk.remediationStatus === 'Completed'}
                        title={title} onClick={() => setOpen('accept')}>
                        <Shield className="h-4 w-4 mr-2" />
                        Accept Risk
                    </Button>

                    {canApproveRisk(risk) && (
                        <Button size="sm" variant="primary" disabled={disabled} title={title} onClick={() => setOpen('approve')}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                        </Button>
                    )}
                    {trailing}
                </div>
            </div>

            {open === 'manage' && (
                <RiskManagementModal
                    key={risk.id}
                    risk={workflowRisk}
                    updateEndpoint={endpoints.update}
                    fieldPrefix={endpoints.fieldPrefix}
                    onClose={close}
                    refreshData={onChanged}
                />
            )}
            {open === 'accept' && (
                <RiskAcceptanceModal
                    show
                    risk={workflowRisk}
                    acceptEndpoint={endpoints.accept}
                    fieldPrefix={endpoints.fieldPrefix}
                    onClose={close}
                    onAccepted={onChanged}
                    riskId={risk.id}
                    riskType={risk.riskType}
                />
            )}
            {open === 'assign' && (
                <AssignRiskModal
                    show
                    risk={workflowRisk}
                    assignEndpoint={endpoints.assign}
                    onClose={close}
                    refreshData={onChanged}
                    riskId={risk.id}
                    riskType={risk.riskType}
                    dealId={risk.dealId || undefined}
                    onAssignSuccess={onAssignSuccess}
                />
            )}
            {open === 'approve' && (
                <RiskApprovalModal
                    show
                    risk={workflowRisk}
                    approveBaseEndpoint={endpoints.approveBase}
                    onClose={close}
                    refreshData={onChanged}
                    riskId={risk.id}
                    riskType={risk.riskType}
                />
            )}
            {open === 'jira' && (
                <CreateJiraIssueModal
                    isOpen
                    onClose={close}
                    remediation={ticketRisk}
                    remediationType={ticketRisk.remediationType}
                    onSuccess={() => onChanged?.()}
                />
            )}
            {open === 'servicenow' && (
                <CreateServiceNowIncidentModal
                    isOpen
                    onClose={close}
                    remediation={ticketRisk}
                    remediationType={ticketRisk.remediationType}
                    onSuccess={() => onChanged?.()}
                />
            )}
        </>
    );
};

export default RiskWorkflowActions;
